import { createUseStyles, Styles } from '@pff-consumer/web-ui/styles'

interface CreateAccountStyles {
  createAccountContainer: Styles
  button: Styles
  mainContainer: Styles
  heading: Styles
  createAccountButton: Styles
  headingText: Styles
  textFieldContainer: Styles
  textFieldLabel: Styles
  email: Styles
  password: Styles
  mandatoryIndicator: Styles
  invalidEmail: Styles
  newsletterCheckboxContainer: Styles
  newsletterCheckbox: Styles
  newsletterCheckboxLabel: Styles
  optionLine: Styles
  socialMediaOptionContainer: Styles
  socialMediaOptionText: Styles
  socialMediaButton: Styles
  signInLink: Styles
  errorMessage: Styles
  newsletterCheckboxContainerSuccess: Styles
  passwordIconContainer: Styles
}

interface CreateAccountStylesProps {
  isSafari: boolean
}

export const useStyles = createUseStyles<CreateAccountStyles, CreateAccountStylesProps>((theme) => {
  const textFieldBaseStyles = {
    fontFamily: 'Roboto',
    fontWeight: '400',
    fontSize: theme.calcUnit(theme.spacing.s4),
    lineHeight: theme.calcUnit(theme.spacing.s6),
    padding: `${theme.calcUnit(theme.spacing.s3)} ${theme.calcUnit(10)}`,
    borderWidth: theme.calcUnit(1),
    borderRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
    marginTop: theme.calcUnit(theme.spacing.s2),
    opacity: 1,
    color: theme.colors.webTextField,
    borderColor: theme.colors.webBorder,
    width: '100%',

    '&:focus': {
      outline: 'none',
      paddingRight: theme.calcUnit(10),
    },
    '&::placeholder': {
      color: theme.colors.webTextFieldPlaceholder,
    },
  }
  return {
    createAccountContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.calcUnit(theme.spacing.s5),
      height: '100vh',
    },
    button: {
      marginTop: theme.calcUnit(theme.spacing.s1),
    },
    mainContainer: {
      width: theme.calcUnit(335),
    },
    heading: {
      fontFamily: 'Barlow',
      fontWeight: '700',
      fontSize: theme.calcUnit(theme.spacing.s6),
      lineHeight: theme.calcUnit(29),
      paddingTop: theme.calcUnit(theme.spacing.s3),
      margin: theme.calcUnit(0),
    },
    headingText: {
      fontFamily: 'Roboto',
      fontWeight: '400',
      fontSize: theme.calcUnit(theme.spacing.s4),
      lineHeight: theme.calcUnit(theme.spacing.s6),
      paddingTop: theme.calcUnit(theme.spacing.s2),
      margin: theme.calcUnit(0),
      color: theme.colors.webShortDescription,
    },
    newsletterCheckboxContainer: {
      color: theme.colors.webFadedTextColor,
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.calcUnit(6),
      marginTop: theme.calcUnit(theme.spacing.s2),
    },
    newsletterCheckboxContainerSuccess: {
      color: theme.colors.webNewsletterSuccessText,
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.calcUnit(6),
      marginTop: theme.calcUnit(theme.spacing.s2),
    },
    newsletterCheckbox: {
      marginTop: theme.calcUnit(2),
      cursor: 'pointer',
    },
    newsletterCheckboxLabel: {
      fontFamily: 'Roboto',
      fontWeight: '400',
      fontSize: theme.calcUnit(theme.spacing.s3),
      lineHeight: theme.calcUnit(theme.spacing.s4),
      display: 'inline-block',
    },
    textFieldContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.calcUnit(theme.spacing.s4),
    },
    textFieldLabel: {
      fontFamily: 'Roboto',
      fontWeight: '400',
      fontSize: theme.calcUnit(14),
      lineHeight: theme.calcUnit(theme.spacing.s5),
    },
    mandatoryIndicator: {
      color: theme.colors.webErrorColor,
    },
    email: {
      ...textFieldBaseStyles,
    },
    password: ({ isSafari }) => ({
      ...textFieldBaseStyles,
      '&:focus': {
        paddingRight: isSafari ? theme.calcUnit(30) : theme.calcUnit(10),
        outline: 'none',
      },
    }),
    invalidEmail: {
      ...textFieldBaseStyles,
      borderColor: `${theme.colors.webErrorColor}`,
    },
    createAccountButton: {
      marginTop: theme.calcUnit(theme.spacing.s6),
      '&:focus, &:hover': {
        background: theme.colors.webButtonHoverBackground,
      },
    },
    optionLine: {
      border: `${theme.calcUnit(1)} solid ${theme.colors.webOptionBorder}`,
      width: '100%',
    },
    socialMediaOptionContainer: {
      display: 'grid',
      gridTemplateColumns: `1fr 1.5fr 1fr`,
      marginTop: theme.calcUnit(theme.spacing.s6),
      alignItems: 'center',
      textAlign: 'center',
    },
    socialMediaOptionText: {
      fontFamily: 'Roboto',
      color: theme.colors.webOptionBorder,
      fontWeight: '400',
      fontSize: theme.calcUnit(theme.spacing.s3),
      lineHeight: theme.calcUnit(theme.spacing.s4),
      display: 'inline-block',
    },
    socialMediaButton: {
      display: 'grid',
      gridTemplateColumns: `1fr 1fr 1fr`,
      marginTop: theme.calcUnit(theme.spacing.s6),
      justifyItems: 'center',
      alignItems: 'center',

      '& button': {
        height: theme.calcUnit(60),
        width: theme.calcUnit(60),
      },

      '& button:hover:not([disabled])': {
        border: `${theme.calcUnit(2)} solid ${theme.colors.webButtonHoverBackground}`,
      },
      '& button:focus:not([disabled])': {
        border: `${theme.calcUnit(2)} solid ${theme.colors.webButtonHoverBackground}`,
      },
    },
    signInLink: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.calcUnit(theme.spacing.s8),
      fontFamily: 'Roboto',
      color: theme.colors.webLinkText,
      fontWeight: '400',
      fontSize: theme.calcUnit(14),
      lineHeight: theme.calcUnit(theme.spacing.s4),

      '& button': {
        color: theme.colors.webActiveTextColor,
        fontWeight: '700',
        marginLeft: theme.calcUnit(theme.spacing.s1),
        textDecoration: 'underline',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        outline: 'inherit',
      },
    },
    errorMessage: {
      display: 'flex',
      alignItems: 'center',
      fontFamily: 'Roboto',
      fontStyle: 'italic',
      color: theme.colors.webErrorColor,
      fontWeight: 400,
      fontSize: theme.calcUnit(theme.spacing.s3),
      lineHeight: theme.calcUnit(theme.spacing.s5),

      '& svg': {
        marginRight: theme.calcUnit(5),
      },
    },
    passwordIconContainer: {
      alignSelf: 'flex-end',
      position: 'absolute',
      cursor: 'pointer',
      background: 'none',
      margin: `${theme.calcUnit(45)} ${theme.calcUnit(theme.spacing.s1)}`,
    },
  }
})
